import { DestroyRef, Injectable } from '@angular/core';
import { Observable, merge } from 'rxjs';
import { ResourceType } from 'src/app/models/api';
import { SendEmailApiModel } from 'src/app/models/api/actions/sendEmailApi.model';
import { HttpService } from '../http/http.service';
import {
  ListUserPermissionsActionOutputs,
  RemoveUserPermissionsActionInputs,
  RemoveUserPermissionsActionOutputs,
} from 'src/app/models/api/actions/userPermissions.model';
import {
  DownloadAttachmentActionInputs as DownloadResourceAttachmentActionInputs,
  DownloadAttachmentActionOutputs as DownloadResourceAttachmentActionOutputs,
} from 'src/app/models/api/actions/downloadAttachment.model';
import { JsonHalCollectionItems, JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { HttpParams } from '@angular/common/http';
import { ActionGetRawResourceOutputs } from 'src/app/models/api/actions/getResourceDefinition.model';
import {
  PowerplatformSetCopilotAuthenticationActionInputs,
  PowerplatformSetCopilotAuthenticationActionOutputs,
} from 'src/app/models/api/actions/powerplatformSetCopilotAuthenticaiton.model';
import { PowerplatformCopilotAuthenticationTypeEnum } from 'src/app/components/actions/powerplatform-actions/set-copilot-authentication/enums/enums';
import { PowerplatformPublishCopilotActionOutputs } from 'src/app/models/api/actions/powerplatformPublishCopilot.model';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { ActionGetRawListOutputs } from 'src/app/models/api/actions/getRawJsonList.model';
import {
  ZenityEntityActionOutputs,
  ZenityEntityActionResultCode,
} from 'src/app/models/api/actions/zenityEntityActions';
import { GetFileActionOutputs } from '../../../models/api/actions/getFile.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ActionDeleteResponseModel {
  alreadyDeleted: boolean;
}

export interface ActionGetResourceDefinitionResponseModel {
  rawJson: string;
}

export interface ActionGetListResourceDefinitionResponseModel {
  raw_json_items: string[];
}

export interface RequiredParamsForActionsHttpRequests {
  integrationId: string;
  resourceId: string;
  resourceType: ResourceType;
  isAdminEndpoint?: boolean;
  resourceStatus?: ResourceStatus;
}

export interface RequiredParamsForAgentStepHttpRequests extends RequiredParamsForActionsHttpRequests {
  stepId: string;
}

export interface RequiredParamsForSetAccessHttpRequests extends RequiredParamsForActionsHttpRequests {
  isChangeAccessActionEnabled: boolean;
}

export interface RequiredParamsForGetActionsOfResourcesRequest {
  integrationId: string;
  resourcesIds: string[];
}

export interface RequiredParamsForGetActionsOfAgentStepsRequest {
  integrationId: string;
  stepIds: string[];
}

export interface RequiredParamsForGetActionsOfViolationsRequest {
  integrationId: string;
  violationsIds: string[];
}

export interface RequiredParamsForViolationActionsHttpRequests extends RequiredParamsForActionsHttpRequests {
  violationId: string;
}

export interface RequiredParamsForChatGptEnterpriseDeleteConversationAction
  extends RequiredParamsForActionsHttpRequests {
  integrationId: string;
  stepId: string;
}

export interface AddOrRemoveOwnerParamsModel extends RequiredParamsForActionsHttpRequests {
  isAddOwner: boolean;
}

export interface SetApplicationQuarantineStateParamsModel extends RequiredParamsForActionsHttpRequests {
  quarantine_status: PpApplicationQuarantineStatus;
}

export interface AutomationActivationParams extends RequiredParamsForActionsHttpRequests {
  action: AutomationActivationActions;
}

export enum AutomationActivationActions {
  start = 'start',
  stop = 'stop',
}

export enum PpApplicationQuarantineStatus {
  quarantined = 'quarantined',
  unquarantined = 'unquarantined',
}

export enum ActionResultCode {
  success = 'success',
  failure = 'failure',
  no_effect = 'noEffect',
}
export interface ResourceActionOutput {
  resultCode: ActionResultCode;
  title: string;
  description: string | null;
}

export interface IActionTypeResponse {
  actionType: ActionTypes;
  isRestricted: boolean;
  restrictedReason: string;
}
export interface ActionResponseModel {
  id: string;
  title: string;
  description: string;
}
export interface ResourceActionsResponseModel {
  resourceId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export interface AgentStepActionsResponseModel {
  stepId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export interface ViolationsActionsResponseModel {
  violationId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export enum ActionStatus {
  enabled = 'enabled',
  disabled = 'disabled',
}

export enum PowerplatformAgentActionAuthenticationMode {
  maker = 'maker', // "author"
  invoker = 'invoker', // "user"
}

export enum PowerplatformAgentActionConfirmationMode {
  strict = 'strict',
  disabled = 'disabled',
}

export interface PowerplatformAgentActionSetAuthenticationModePayload {
  authenticationMode: PowerplatformAgentActionAuthenticationMode;
}

export interface PowerplatformAgentActionSetConfirmationModePayload {
  confirmationMode: PowerplatformAgentActionConfirmationMode;
}

@Injectable({
  providedIn: 'root',
})
export class ActionsHttpServiceService {
  private readonly _actionsBaseUrl: string = 'actions';

  constructor(
    private readonly _httpService: HttpService,
    private readonly _destroyRef: DestroyRef
  ) {}

  public getActionsOfPlaybookTrigger(
    integrationId: string,
    trigger: PlaybookTrigger
  ): Observable<JsonHalCollectionItems<IActionTypeResponse>> {
    const url = `playbooks/actions?integrationId=${integrationId}`;
    return this._httpService.post<PlaybookTrigger, JsonHalCollectionItems<IActionTypeResponse>>(url, trigger);
  }

  public getActionsOfResources(
    params: RequiredParamsForGetActionsOfResourcesRequest
  ): Observable<JsonHalCollection<ResourceActionsResponseModel>> {
    // URL is v1/inventory/integrations/{integrationId}/resources/actions and the query params is resources ids of the integration and looks like this: ?resourceIds={resourceId1},{resourceId2}
    const url = `inventory/integrations/${params.integrationId}/resources/actions`;
    const httpParams = new HttpParams().append('resourcesIds', params.resourcesIds.join(','));
    return this._httpService.get<JsonHalCollection<ResourceActionsResponseModel>>(url, undefined, httpParams);
  }

  public getActionsOfViolations(
    params: RequiredParamsForGetActionsOfViolationsRequest
  ): Observable<JsonHalCollection<ViolationsActionsResponseModel>> {
    // URL is v1/assessment/integrations/{integrationId}/violations/actions and the query params is violations ids of the integration and looks like this: ?violationsIds={violationId1},{violationId2}
    const url = `assessment/integrations/${params.integrationId}/violations/actions`;
    const httpParams = new HttpParams().append('violationsIds', params.violationsIds.join(','));
    return this._httpService.get<JsonHalCollection<ViolationsActionsResponseModel>>(url, undefined, httpParams);
  }

  public getActionsOfAgentSteps(
    params: RequiredParamsForGetActionsOfAgentStepsRequest
  ): Observable<JsonHalCollection<AgentStepActionsResponseModel>> {
    // URL is v1-beta/detection/integrations/{integrationId}/agentSteps/actions and the query params is step ids of the integration and looks like this: ?stepIds={stepId1},{stepId2}
    const url = `detection/integrations/${params.integrationId}/agentSteps/actions`;
    const httpParams = new HttpParams().append('stepIds', params.stepIds.join(','));
    return this._httpService.get<JsonHalCollection<AgentStepActionsResponseModel>>(url, undefined, httpParams, true);
  }

  public addOwner(params: RequiredParamsForActionsHttpRequests, email: string): Observable<boolean> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/owner`;
    return this._httpService.post<{ email: string }, boolean>(url, {
      email: email,
    });
  }

  public removeOwner(params: RequiredParamsForActionsHttpRequests, email: string): Observable<boolean> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/owner`;
    return this._httpService.delete_with_body<{ email: string }, boolean>(url, { email: email });
  }

  public secureFlowSteps(params: RequiredParamsForViolationActionsHttpRequests): Observable<ResourceActionOutput> {
    const url = `assessment/integrations/${params.integrationId}/violations/${params.violationId}/actions/powerplatformSecureFlowSteps:execute`;
    return this._httpService.post(url, {});
  }

  public getFlowDefinition(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ActionGetResourceDefinitionResponseModel> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/getDefinition`;
    return this._httpService.get<ActionGetResourceDefinitionResponseModel>(url);
  }

  public getRawResource(integrationId: string, resourceId: string): Observable<ActionGetRawResourceOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/getRawResource:execute`;
    const body = '';
    return this._httpService.post<string, ActionGetRawResourceOutputs>(url, body);
  }

  public getFlowRuns(integrationId: string, resourceId: string): Observable<ActionGetRawListOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/powerplatformGetRawFlowRuns:execute`;
    const body = '';
    return this._httpService.post<string, ActionGetRawListOutputs>(url, body);
  }

  public deleteResource(params: RequiredParamsForActionsHttpRequests): Observable<ActionDeleteResponseModel> {
    let url = `${this._actionsBaseUrl}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}`;

    if (params.resourceType === ResourceType.recipe) {
      url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/recipe/${params.resourceId}`;
    }
    return this._httpService.delete<ActionDeleteResponseModel>(url);
  }

  public agentDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentDeleteAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionDeleteAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionEnforceUserAuthenticationAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionEnforceUserAuthenticationAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionEnforceUserConfirmationAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionEnforceUserConfirmationAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionEnableAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionEnableAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionDisableAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionDisableAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionUpdateAuthenticationMode(
    params: RequiredParamsForActionsHttpRequests,
    authenticationMode: PowerplatformAgentActionAuthenticationMode
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionSetAuthenticationModeAction:execute`;
    const body: PowerplatformAgentActionSetAuthenticationModePayload = {
      authenticationMode: authenticationMode,
    };
    return this._httpService.post(url, body);
  }

  public agentActionUpdateConfirmationMode(
    params: RequiredParamsForActionsHttpRequests,
    confirmationMode: PowerplatformAgentActionConfirmationMode
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentActionSetConfirmationModeAction:execute`;
    const body: PowerplatformAgentActionSetConfirmationModePayload = {
      confirmationMode: confirmationMode,
    };
    return this._httpService.post(url, body);
  }

  public agentActionEnableWebBrowsing(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentEnableWebBrowsingAction:execute`;
    return this._httpService.post(url, {});
  }

  public agentActionDisableWebBrowsing(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/powerplatformAgentDisableWebBrowsingAction:execute`;
    return this._httpService.post(url, {});
  }

  public setResourceQuarantineState(params: SetApplicationQuarantineStateParamsModel): Observable<boolean> {
    const url = `${this._actionsBaseUrl}/integration/${params.integrationId}/application/${params.resourceId}/quarantine-state?status=${params.quarantine_status}`;

    return this._httpService.put<boolean>(url);
  }

  public gptDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseDeleteGptAction:execute`;
    return this._httpService.post(url, {});
  }

  public gptIsolateAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseIsolateGptAction:execute`;
    return this._httpService.post(url, {});
  }

  public gptDeleteGptKnowledgeAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseDeleteGptKnowledgeAction:execute`;
    return this._httpService.post(url, {});
  }

  public gptGetKnowledgeFileUrlAction(params: RequiredParamsForActionsHttpRequests): Observable<GetFileActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseGetGptKnowledgeFileUrlAction:execute`;
    const response = this._httpService.post<object, GetFileActionOutputs>(url, {});
    response.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((res) => {
      if (res.resultCode === ZenityEntityActionResultCode.success) {
        window.open(res.url, '_blank');
      }
    });
    return response;
  }

  public gptUserCanvasDeleteAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseDeleteUserCanvasAction:execute`;
    return this._httpService.post(url, {});
  }

  public gptGetUserCanvasAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    const url = `inventory/integrations/${params.integrationId}/resources/${params.resourceId}/actions/chatGptEnterpriseGetUserCanvasAction:execute`;
    return this._httpService.post(url, {});
  }

  public gptDeleteConversationAction(
    params: RequiredParamsForChatGptEnterpriseDeleteConversationAction
  ): Observable<ZenityEntityActionOutputs> {
    const url = `detection/integrations/${params.integrationId}/agentSteps/${params.stepId}/actions/chatGptEnterpriseDeleteConversationAction:execute`;
    return this._httpService.post(url, {}, undefined, undefined, true);
  }

  public automationActivation(params: AutomationActivationParams, resourceType: ResourceType): Observable<boolean> {
    const resourceTypeStr = resourceType == ResourceType.flow ? 'flow' : 'recipe';
    const url = `${this._actionsBaseUrl}/integration/${params.integrationId}/${resourceTypeStr}/${params.resourceId}?action=${params.action}`;

    return this._httpService.put<boolean>(url);
  }

  public sendEmail(integrationId: string, emailData: SendEmailApiModel): Observable<boolean> {
    const url = `${this._actionsBaseUrl}/send-email/integration/${integrationId}`;

    return this._httpService.post<SendEmailApiModel, boolean>(url, emailData);
  }

  public getUserAccessDetails(integrationId: string, resourceId: string): Observable<ListUserPermissionsActionOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/listResourcePermissions:execute`;
    const body = ''; // Action does not receive additional inputs in body
    return this._httpService.post<string, ListUserPermissionsActionOutputs>(url, body);
  }

  public removeUserAccess(
    integrationId: string,
    resourceId: string,
    userIds: string[]
  ): Observable<RemoveUserPermissionsActionOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/removeResourcePermissions:execute`;
    const observables = userIds.map((userId) =>
      this._httpService.post<RemoveUserPermissionsActionInputs, RemoveUserPermissionsActionOutputs>(url, {
        identity_platform_resource_id: userId,
      })
    );
    return merge(...observables);
  }

  public downloadAttachment(
    integrationId: string,
    resourceId: string,
    attachmentId: string
  ): Observable<DownloadResourceAttachmentActionOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/downloadResourceAttachment:execute`;
    const body: DownloadResourceAttachmentActionInputs = {
      attachmentId: attachmentId,
    };
    return this._httpService.post<DownloadResourceAttachmentActionInputs, DownloadResourceAttachmentActionOutputs>(
      url,
      body
    );
  }

  public powerplatformSetCopilotAuthentication(
    integrationId: string,
    resourceId: string,
    copilotAuthentication: PowerplatformCopilotAuthenticationTypeEnum
  ): Observable<PowerplatformSetCopilotAuthenticationActionOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/powerplatformSetCopilotAuthentication:execute`;
    const body: PowerplatformSetCopilotAuthenticationActionInputs = {
      authenticationType: copilotAuthentication,
    };
    return this._httpService.post<
      PowerplatformSetCopilotAuthenticationActionInputs,
      PowerplatformSetCopilotAuthenticationActionOutputs
    >(url, body);
  }

  public powerplatformPublishCopilot(
    integrationId: string,
    resourceId: string
  ): Observable<PowerplatformSetCopilotAuthenticationActionOutputs> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/powerplatformPublishCopilot:execute`;
    return this._httpService.post<string, PowerplatformPublishCopilotActionOutputs>(url, '');
  }

  private _getBaseUrl(params: RequiredParamsForActionsHttpRequests): string {
    let appBaseUrl = `${this._actionsBaseUrl}`;

    if (params.isAdminEndpoint) {
      appBaseUrl = `${this._actionsBaseUrl}/admin`;
    }

    return appBaseUrl;
  }
}
